import { ImportHistoryApi } from '../api/importHistoryApi';
import { Filters, ProcessStatus, Transaction, TransactionResponse } from '../interfaces/ImportHistory';
import { FILTERS, INITIAL_DATE_RANGE, INITIAL_FILTERS } from '../shared/constants/ImportHistoryConstants';
import {
	EMPTY_ARRAY,
	EMPTY_ARRAY_STRING,
	EMPTY_STRING,
	INITIAL_PAGE_STATE,
	INITIAL_SORT_COLUMN_DESC,
} from '../shared/constants/constants';
import { FileContext } from './FileContext';
import { OrganizationContext } from './OrganizationContext';
import React, { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface BatchDeterminationContextProps {
	selectedTransaction: Transaction | undefined;
	importHistoryData: Transaction[];
	forceRefreshingTemplateTableId: number;
	isLoading: boolean;
	filters: Filters;
	processStatuses: ProcessStatus[];
	profiles: string[];
	setSelectedTransaction: Dispatch<SetStateAction<Transaction | undefined>>;
	forceRefreshingTemplateTable: () => void;
	onClose: () => void;
	getImportData: () => void;
	handleFiltersChange: (filterName: string, value: any) => void;
}

export const BatchDeterminationContext = createContext<BatchDeterminationContextProps>({
	selectedTransaction: undefined,
	forceRefreshingTemplateTableId: 0,
	importHistoryData: EMPTY_ARRAY,
	isLoading: false,
	filters: INITIAL_FILTERS,
	processStatuses: EMPTY_ARRAY,
	profiles: EMPTY_ARRAY,
	setSelectedTransaction: () => null,
	forceRefreshingTemplateTable: () => null,
	onClose: () => null,
	getImportData: () => null,
	handleFiltersChange: () => null,
});

export const BatchDeterminationContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [forceRefreshingHistoryTableId, setForceRefreshingHistoryTableId] = useState<number>(0);
	const [forceRefreshingTemplateTableId, setForceRefreshingTemplateTableId] = useState<number>(0);
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
	const [importHistoryData, setImportHistoryData] = useState<Transaction[]>(EMPTY_ARRAY);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
	const [processStatuses, setProcessStatuses] = useState<ProcessStatus[]>(EMPTY_ARRAY);
	const [profiles, setProfiles] = useState<string[]>([]);
	const { clearSelectedFiles } = useContext(FileContext);
	const { selectedOrg } = useContext(OrganizationContext);
	const navigate = useNavigate();

	const getImportData = () => {
		const { pageState, sortedColumn, dateRange, search, status, profiles, startDate } = filters;
		setIsLoading(true);

		ImportHistoryApi.getImportHistory(
			pageState.page,
			pageState.perPage,
			sortedColumn,
			dateRange,
			search,
			status,
			profiles,
			startDate
		)
			.then((response: TransactionResponse) => {
				if (response?.items) {
					setImportHistoryData(response.items);
					setFilters(filters => ({
						...filters,
						pageState: { ...filters.pageState, totalEntries: response.pageState.totalEntries },
					}));
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleFiltersChange = (filterName: string, value: any) => {
		setFilters(filter => ({ ...filter, [filterName]: value }));
		setForceRefreshingHistoryTableId(value => value + 1);
	};

	const forceRefreshingTemplateTable = () => {
		setForceRefreshingTemplateTableId(forceRefreshingTemplateTableId + 1);
	};

	const onClose = () => {
		sessionStorage.removeItem('selectedTransaction');
		sessionStorage.removeItem('processId');
		setSelectedTransaction(undefined);
		clearSelectedFiles();
		navigate('/');
	};

	useEffect(() => {
		if (selectedOrg) {
			handleFiltersChange(FILTERS.PAGE_STATE, { page: INITIAL_PAGE_STATE.page, perPage: INITIAL_PAGE_STATE.perPage });
			handleFiltersChange(FILTERS.SORTED_COLUMN, INITIAL_SORT_COLUMN_DESC);
			handleFiltersChange(FILTERS.DATE_RANGE, INITIAL_DATE_RANGE);
			handleFiltersChange(FILTERS.STATUS, EMPTY_ARRAY_STRING);
			handleFiltersChange(FILTERS.SEARCH, EMPTY_STRING);

			ImportHistoryApi.getProcessStatus().then((status: ProcessStatus[]) => setProcessStatuses(status));
			ImportHistoryApi.getProfiles().then(profiles => {
				const allProfileName = profiles.map(profile => profile.name);
				allProfileName.unshift('Custom Import');
				setProfiles(allProfileName);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOrg]);

	useEffect(() => {
		if (selectedOrg) {
			getImportData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceRefreshingHistoryTableId]);

	return (
		<BatchDeterminationContext.Provider
			value={{
				selectedTransaction,
				forceRefreshingTemplateTableId,
				importHistoryData,
				isLoading,
				filters,
				processStatuses,
				profiles,
				forceRefreshingTemplateTable,
				onClose,
				setSelectedTransaction,
				getImportData,
				handleFiltersChange,
			}}
		>
			{children}
		</BatchDeterminationContext.Provider>
	);
};
